import React, { useEffect } from "react";
import SectionHeader from "@/components/common/SectionHeader";
import { CheckCircle2 } from "lucide-react";

const VALUES = [
  { title: "Quality Focus", body: "Product-first thinking. Buyer specifications guide every sourcing decision." },
  { title: "Transparent Communication", body: "Clear updates on availability, pricing structure and shipment status." },
  { title: "Reliable Sourcing", body: "Working with dependable supply partners across India's key agricultural regions." },
  { title: "Professional Execution", body: "Documentation, packaging and dispatch handled with commercial discipline." },
  { title: "Long-Term Partnerships", body: "We prioritise trust and consistency over one-time transactions." },
];

const SECTIONS = [
  {
    eyebrow: "Company Overview",
    title: "A growing agricultural commodity trading and sourcing business",
    body: "Aaha Kanakam Traders works with buyers and supply partners to source and coordinate agricultural commodities according to product specifications, quantity requirements, packaging preferences and delivery needs. Our approach is based on transparent communication, supplier coordination, quality awareness and dependable trade execution.",
  },
  {
    eyebrow: "Business Model",
    title: "Merchant exporter and B2B sourcing partner",
    body: "As a merchant exporter and importer, we bridge international buyers with reliable Indian supply partners. Rice is our primary category today; we are actively expanding into pulses, cereals, millets, spices and oilseeds as buyer demand and supplier networks develop.",
  },
  {
    eyebrow: "Sourcing Approach",
    title: "Specification-first, buyer-driven procurement",
    body: "Every enquiry starts with a clear brief — variety, grade, packaging, destination, target price and shipment timing. From that brief, we identify suitable supply partners, coordinate samples where needed, and confirm packaging and documentation before dispatch.",
  },
  {
    eyebrow: "Long-Term Vision",
    title: "Becoming a broad-based agricultural trading partner from India",
    body: "Our long-term goal is to serve international buyers as a professional, credible and scalable sourcing partner across a wide range of Indian agricultural commodities — supported by strong supplier relationships and disciplined execution.",
  },
];

export default function AboutPage() {
  useEffect(() => {
    document.title = "About — Aaha Kanakam Traders";
  }, []);
  return (
    <div data-testid="about-page">
      <section className="bg-forest text-white py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
          <div className="eyebrow text-gold mb-4">About</div>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-tight max-w-3xl">
            Reliable sourcing, quality-focused procurement, professional trade execution.
          </h1>
        </div>
      </section>

      <section className="py-20 md:py-24 bg-ivory">
        <div className="max-w-5xl mx-auto px-4 sm:px-8 lg:px-12 space-y-16">
          {SECTIONS.map((s) => (
            <div key={s.eyebrow} className="grid md:grid-cols-12 gap-8 items-start">
              <div className="md:col-span-4">
                <div className="eyebrow">{s.eyebrow}</div>
              </div>
              <div className="md:col-span-8">
                <h3 className="font-serif text-2xl md:text-3xl text-forest leading-tight mb-4">{s.title}</h3>
                <p className="text-base text-slateneutral leading-relaxed">{s.body}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="py-20 md:py-24 bg-surface">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
          <SectionHeader eyebrow="Core Values" title="The principles that shape our work" />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {VALUES.map((v) => (
              <div key={v.title} className="p-7 bg-white border border-borderSubtle card-hover">
                <CheckCircle2 className="text-gold-600 mb-4" size={22} />
                <div className="font-serif text-xl text-forest mb-2">{v.title}</div>
                <p className="text-sm text-slateneutral leading-relaxed">{v.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
